import { useState, useEffect, useMemo, useRef } from 'react';
import { fetchSelectOptions } from '@/components/formBuilder/helpers/formHelpers';
import { KAutoComplete } from '@/ui-library/components/organisms/KFields/KAutoComplete';
import { CustomValueContainer } from '@/components/ats/EmailTemplates/EmailTemplates';

// Helper function to find a matching template from a list of possible names (case-insensitive)
const findMatchingTemplate = (templates, initialValue) => {
  if (!initialValue) return null;

  // If initialValue is an array, try to match any of the names in order
  if (Array.isArray(initialValue)) {
    for (const name of initialValue) {
      const match = templates.find(
        (template) =>
          template?.title?.toLowerCase() === name?.toLowerCase() ||
          template?.name?.toLowerCase() === name?.toLowerCase()
      );
      if (match) return match;
    }
    return null;
  }

  // If initialValue is a string, do a simple case-insensitive match
  return templates.find(
    (template) =>
      template?.title?.toLowerCase() === initialValue?.toLowerCase() ||
      template?.name?.toLowerCase() === initialValue?.toLowerCase()
  );
};

export const ChooseSurveyTemplate = ({
  selectOnChange = () => {},
  channel = 'email',
  category = 'candidate_job_stage_action_notice',
  setDefault = false,
  styleVersion = 'v1',
  initialValue = null,
  surveyType = 'candidate',
  selectedTemplate = null,
}) => {
  const [templates, setTemplates] = useState([]);
  const [, setIsLoading] = useState(false);
  const lastAppliedValue = useRef(null);

  // Normalize initialValue for comparison (convert array to string for ref tracking)
  const initialValueKey = Array.isArray(initialValue)
    ? initialValue.join('|')
    : initialValue;

  const buildEndPoint = () => {
    // Build endpoint for email templates (ats_comm_templates) with category filter
    // This fetches email templates that have subject and body
    if (typeof category === 'string') {
      return `/ats_comm_templates?filter_by[channel]=${channel}&filter_by[category][]=${category}&items=200&filter_by[name]=`;
    }

    if (Array.isArray(category)) {
      const categories = category
        .map((cat) => `&filter_by[category][]=${cat}`)
        .join('');
      return `/ats_comm_templates?filter_by[channel]=${channel}${categories}&items=200&filter_by[name]=`;
    }

    return `/ats_comm_templates?filter_by[channel]=${channel}&filter_by[category][]=survey_notice&items=200&filter_by[name]=`;
  };

  // Fetch email templates on mount
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        setIsLoading(true);
        const apiConfig = {
          endPoint: buildEndPoint(),
        };
        const list = await fetchSelectOptions(apiConfig);

        if (!isMounted) return;

        setTemplates(list);

        // Apply initial selection ONLY if:
        // 1. No template already selected from parent
        // 2. initialValue is provided (not null - meaning not edit mode)
        if (
          list?.length > 0 &&
          !lastAppliedValue.current &&
          !selectedTemplate?.id &&
          initialValue
        ) {
          const matchingTemplate = findMatchingTemplate(list, initialValue);

          if (matchingTemplate) {
            selectOnChange(matchingTemplate);
            lastAppliedValue.current = initialValueKey;
          } else if (setDefault) {
            selectOnChange(list[0]);
            lastAppliedValue.current = 'default';
          }
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    fetchData();

    return () => {
      isMounted = false;
    };
  }, [category, channel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute the initial selected option for KAutoComplete
  // This should be a pure computation without side effects
  const initialSelectedOption = useMemo(() => {
    // If selectedTemplate is provided (from parent state or activity data)
    if (selectedTemplate?.id) {
      // In edit mode, find the matching template from the fetched list by ID
      // This ensures we have the full template object with 'name' for display
      if (templates?.length > 0) {
        const matchingTemplate = templates.find(
          (t) => t?.id === selectedTemplate?.id
        );
        if (matchingTemplate) {
          return matchingTemplate;
        }
      }
      // Fallback to selectedTemplate if not found in list
      // Add 'name' from 'email_template_name' or 'title' if missing (for display purposes)
      return {
        ...selectedTemplate,
        name: selectedTemplate?.name || selectedTemplate?.email_template_name || selectedTemplate?.title || 'Selected Template',
      };
    }

    if (templates?.length === 0) return null;

    // Return matching template based on initialValue (create mode only)
    if (initialValue) {
      return findMatchingTemplate(templates, initialValue) || null;
    }

    // Handle setDefault
    if (setDefault) {
      return templates[0] || null;
    }

    return null;
  }, [templates, initialValue, setDefault, selectedTemplate]);

  return (
    <KAutoComplete
      key={`${initialValueKey}-${selectedTemplate?.id || ''}-${initialSelectedOption?.id || ''}-${initialSelectedOption?.name || ''}`}
      item={{
        name: 'surveyTemplates',
        label: 'Select survey',
        type: 'autoComplete',
        useInput: true,
        isRequired: true,
        isMulti: false,
        placeholder: `Select a survey`,
        labelKey: 'name',
        valueKey: 'id',
        customOnChange: selectOnChange,
        styleVersion: styleVersion,
        options: templates,
        updateEndpointOnChange: true,
        initialSelectedOption: initialSelectedOption,
        fetchConfig: {
          method: 'get',
          endPoint: buildEndPoint(),
          processResponse: (res) => {
            const options = res?.data?.map((item) => ({
              label: item?.name,
              value: item?.id,
              ...item,
            }));

            return options;
          },
        },
        components: {
          ValueContainer: CustomValueContainer,
        },
      }}
    />
  );
};
